import React, { useState, useEffect, useMemo } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MagnifyingGlass, ArrowLeft, CaretDown, Wrench, Warning, CheckCircle, Clock, PaperPlaneTilt, Eye } from '@phosphor-icons/react';

const STATUS_LABELS = {
  demande_envoyee: 'Demande envoyée', en_cours_analyse: 'En cours d\'analyse',
  en_attente_informations: 'En attente d\'infos', solution_validee: 'Solution validée',
  en_attente_retour: 'Attente retour', produit_recu: 'Produit reçu',
  en_fabrication: 'En fabrication', expediee: 'Expédiée', cloturee: 'Clôturée',
  annulee: 'Annulée'
};
const STATUS_COLORS = {
  demande_envoyee: 'bg-blue-100 text-blue-800', en_cours_analyse: 'bg-amber-100 text-amber-800',
  en_attente_informations: 'bg-orange-100 text-orange-800', solution_validee: 'bg-emerald-100 text-emerald-800',
  en_attente_retour: 'bg-purple-100 text-purple-800', produit_recu: 'bg-indigo-100 text-indigo-800',
  en_fabrication: 'bg-yellow-100 text-yellow-800', expediee: 'bg-teal-100 text-teal-800',
  cloturee: 'bg-zinc-200 text-zinc-600',
  annulee: 'bg-rose-100 text-rose-700'
};
const DECISION_LABELS = {
  refabrication_gratuite: 'Refab. gratuite', refabrication_payante: 'Refab. payante',
  geste_commercial: 'Geste commercial'
};
const REQUEST_TYPE_LABELS = {
  refabrication_identique: 'Refab. identique', refabrication_modifiee: 'Refab. modifiée',
  retouche: 'Retouche', casse: 'Casse', usure: 'Usure', inconfort: 'Inconfort',
  mauvaise_tenue: 'Mauvaise tenue', probleme_acoustique: 'Pb acoustique',
  defaut: 'Défaut', autre: 'Autre'
};
const SAV_STATUSES = [
  'demande_envoyee', 'en_cours_analyse', 'en_attente_informations',
  'solution_validee', 'en_attente_retour', 'produit_recu',
  'en_fabrication', 'expediee', 'cloturee', 'annulee'
];

export default function AdminSavModule() {
  const [savList, setSavList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [decisionFilter, setDecisionFilter] = useState('all');
  const [qualityOnly, setQualityOnly] = useState(false);
  const [selectedSav, setSelectedSav] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => { fetchSav(); }, []);

  const fetchSav = async () => {
    try {
      const res = await axios.get(`${API}/sav`);
      setSavList(res.data);
    } catch { toast.error('Erreur chargement SAV'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    return savList.filter(s => {
      if (qualityOnly && !s.suspected_nonconformity) return false;
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (typeFilter !== 'all' && s.request_type !== typeFilter) return false;
      if (decisionFilter !== 'all') {
        if (decisionFilter === 'sans' && s.decision) return false;
        if (decisionFilter !== 'sans' && s.decision?.type !== decisionFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        return (s.sav_number || '').toLowerCase().includes(q) ||
          (s.client_name || '').toLowerCase().includes(q) ||
          (s.product_name || '').toLowerCase().includes(q) ||
          (s.order_patient_ref || '').toLowerCase().includes(q) ||
          (s.serial_number || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [savList, statusFilter, typeFilter, decisionFilter, search, qualityOnly]);

  const openDetail = (sav) => { setSelectedSav(sav); setDetailOpen(true); };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const counters = useMemo(() => ({
    total: savList.length,
    open: savList.filter(s => s.status !== 'cloturee').length,
    urgent: savList.filter(s => s.urgency === 'urgent' && s.status !== 'cloturee').length,
    sans_decision: savList.filter(s => !s.decision && s.status !== 'cloturee').length,
    quality: savList.filter(s => s.suspected_nonconformity && !(s.quality_review_status || '').startsWith('clos')).length
  }), [savList]);

  return (
    <div data-testid="admin-sav-module">
      {/* Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider">Total SAV</p>
          <p className="font-heading font-bold text-2xl">{counters.total}</p>
        </div>
        <button type="button" onClick={() => setQualityOnly(v => !v)} className={`text-left border p-4 rounded-sm transition-colors ${qualityOnly ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-red-200 hover:bg-red-50'}`} data-testid="sav-quality-filter-btn" title="Liste de traitement qualité / réglementaire — suspicion de non-conformité">
          <p className={`text-xs uppercase tracking-wider ${qualityOnly ? 'text-red-100' : 'text-red-700'}`}>Qualité / réglementaire</p>
          <p className={`font-heading font-bold text-2xl ${qualityOnly ? 'text-white' : 'text-red-700'}`} data-testid="sav-quality-count">{counters.quality}</p>
        </button>
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider">En cours</p>
          <p className="font-heading font-bold text-2xl text-amber-600">{counters.open}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider">Urgents</p>
          <p className="font-heading font-bold text-2xl text-rose-600">{counters.urgent}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider">Sans décision</p>
          <p className="font-heading font-bold text-2xl text-orange-600">{counters.sans_decision}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Rechercher SAV, client, produit..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="sav-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] rounded-sm bg-white" data-testid="sav-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tous statuts</SelectItem>
            {SAV_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px] rounded-sm bg-white" data-testid="sav-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tous types</SelectItem>
            {Object.entries(REQUEST_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={decisionFilter} onValueChange={setDecisionFilter}>
          <SelectTrigger className="w-[160px] rounded-sm bg-white" data-testid="sav-filter-decision"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Toutes décisions</SelectItem>
            <SelectItem value="sans">Sans décision</SelectItem>
            {Object.entries(DECISION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* SAV List */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-zinc-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">
            <Wrench size={40} className="mx-auto mb-3 text-zinc-300" />
            <p>Aucune demande SAV trouvée</p>
          </div>
        ) : (
          <Table className="dense-table">
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="label-overline">N° SAV</TableHead>
                <TableHead className="label-overline">Date</TableHead>
                <TableHead className="label-overline">Client</TableHead>
                <TableHead className="label-overline hidden md:table-cell">Produit</TableHead>
                <TableHead className="label-overline hidden lg:table-cell">N° Série</TableHead>
                <TableHead className="label-overline">Type</TableHead>
                <TableHead className="label-overline">Statut</TableHead>
                <TableHead className="label-overline hidden sm:table-cell">Décision</TableHead>
                <TableHead className="label-overline">Priorité</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(sav => (
                <TableRow key={sav.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => openDetail(sav)} data-testid={`sav-row-${sav.id}`}>
                  <TableCell className="font-mono font-bold text-xs">
                    {sav.sav_number}
                    {sav.suspected_nonconformity && <span className="block mt-0.5 text-[9px] font-sans font-semibold uppercase px-1 py-0.5 bg-red-100 text-red-700 rounded-sm w-fit" data-testid={`sav-quality-badge-${sav.id}`}>Suspicion NC</span>}
                  </TableCell>
                  <TableCell className="text-xs">{formatDate(sav.created_at)}</TableCell>
                  <TableCell className="text-sm">
                    <div>{sav.client_name}</div>
                    {sav.company_name && <div className="text-xs text-zinc-400">{sav.company_name}</div>}
                  </TableCell>
                  <TableCell className="text-xs hidden md:table-cell">{sav.product_name}</TableCell>
                  <TableCell className="font-mono text-xs hidden lg:table-cell">{sav.serial_number || '—'}</TableCell>
                  <TableCell><span className="text-xs">{REQUEST_TYPE_LABELS[sav.request_type] || sav.request_type}</span></TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[sav.status] || ''}`}>
                      {STATUS_LABELS[sav.status] || sav.status}
                    </span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {sav.decision ? (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${sav.decision.type === 'refabrication_gratuite' ? 'bg-emerald-100 text-emerald-700' : sav.decision.type === 'refabrication_payante' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>
                        {DECISION_LABELS[sav.decision.type]}
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {sav.urgency === 'urgent' ? (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-sm">Urgent</span>
                    ) : (
                      <span className="text-[10px] text-zinc-400">Normal</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail Dialog */}
      {selectedSav && (
        <SavDetailDialog
          open={detailOpen}
          onOpenChange={(v) => { setDetailOpen(v); if (!v) setSelectedSav(null); }}
          sav={selectedSav}
          onRefresh={() => { fetchSav(); setDetailOpen(false); setSelectedSav(null); }}
        />
      )}
    </div>
  );
}


// ========== Traitement qualité / réglementaire (suspicion de non-conformité, action corrective) ==========
const QR_LABELS = { a_traiter: 'À traiter', en_cours: 'En cours', clos_conforme: 'Clos — dispositif conforme', clos_non_conforme: 'Clos — non-conformité confirmée' };

function QualityReviewBlock({ sav, onRefresh }) {
  const [suspected, setSuspected] = useState(!!sav.suspected_nonconformity);
  const [qrStatus, setQrStatus] = useState(sav.quality_review_status || 'a_traiter');
  const [notes, setNotes] = useState(sav.quality_review_notes || '');
  const [corrective, setCorrective] = useState(sav.corrective_action || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sav/${sav.id}/quality-review`, { suspected_nonconformity: suspected, quality_review_status: suspected ? qrStatus : undefined, quality_review_notes: notes, corrective_action: corrective });
      toast.success('Traitement qualité enregistré'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };
  return (
    <div className={`border p-4 rounded-sm ${suspected ? 'bg-red-50 border-red-200' : 'bg-white border-zinc-200'}`} data-testid="sav-bloc-qualite">
      <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200 flex items-center justify-between">Traitement qualité / réglementaire
        {sav.suspected_nonconformity && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-600 text-white rounded-sm">Suspicion NC · {QR_LABELS[sav.quality_review_status] || sav.quality_review_status || '—'}</span>}
      </h3>
      <div className="space-y-3 text-sm">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={suspected} onChange={e => setSuspected(e.target.checked)} className="mt-1 h-4 w-4 accent-red-600" data-testid="sav-admin-suspected-checkbox" />
          <span><span className="font-medium">Suspicion de non-conformité du dispositif</span><span className="block text-xs text-zinc-500">Identifie le dossier dans la liste de traitement qualité / réglementaire — distinct de l'éligibilité commerciale, sans déclaration automatique.</span></span>
        </label>
        {suspected && (
          <div>
            <span className="text-zinc-400 text-xs">Statut de traitement</span>
            <Select value={qrStatus} onValueChange={setQrStatus}>
              <SelectTrigger className="rounded-sm bg-white mt-1" data-testid="sav-qr-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{Object.entries(QR_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div><span className="text-zinc-400 text-xs">Notes d'analyse qualité</span><Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="rounded-sm mt-1 bg-white text-sm" data-testid="sav-qr-notes" /></div>
        <div><span className="text-zinc-400 text-xs">Action corrective</span><Textarea value={corrective} onChange={e => setCorrective(e.target.value)} rows={2} placeholder="Action corrective réalisée / décidée" className="rounded-sm mt-1 bg-white text-sm" data-testid="sav-corrective-action" />
          {sav.corrective_action_at && <p className="text-[11px] text-zinc-500 mt-0.5">Dernière mise à jour : {new Date(sav.corrective_action_at).toLocaleString('fr-FR')} · {sav.corrective_action_by_name}</p>}
        </div>
        {sav.refabrication_order_id && <p className="text-xs text-zinc-600">Refabrication liée : commande <span className="font-mono">{sav.refabrication_order_id.slice(0, 8).toUpperCase()}</span> — nouveau(x) dispositif(s) avec nouvel identifiant HAMAC, sans héritage du lot / des opérations / du CQ-01 d'origine.</p>}
        <Button size="sm" onClick={save} disabled={saving} className="rounded-sm bg-zinc-900 text-white" data-testid="sav-qr-save">Enregistrer le traitement qualité</Button>
      </div>
    </div>
  );
}


// ========== SAV Detail Dialog ==========
function SavDetailDialog({ open, onOpenChange, sav, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [status, setStatus] = useState(sav.status);
  const [statusComment, setStatusComment] = useState('');
  const [analysis, setAnalysis] = useState(sav.analysis || { internal_comment: '', estimated_cause: '', needs_return: false, assigned_to: '', assigned_name: '' });
  const [decision, setDecision] = useState(sav.decision || { type: sav.suggested_decision || '', justification: '', amount_ht: 0, tva: 20, fees: 0, discount_amount: 0 });
  // Par défaut, à la création SAV par un client, le backend pose is_internal_error=true
  // (perte de prime tech automatique). L'admin peut décocher ici pour rendre la prime au technicien.
  const [isInternalError, setIsInternalError] = useState(sav.is_internal_error !== false);
  const [saving, setSaving] = useState(false);
  const [finalizeNote, setFinalizeNote] = useState('');
  const [finalizingUpload, setFinalizingUpload] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideData, setOverrideData] = useState({ type: '', justification: '', amount_ht: 0, tva: 20, fees: 0, discount_amount: 0, override_reason: '' });
  const finalizeFileRef = React.useRef(null);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const handleStatusChange = async () => {
    if (status === sav.status) return;
    setSaving(true);
    try {
      await axios.put(`${API}/sav/${sav.id}/status`, { status, comment: statusComment });
      toast.success('Statut mis à jour');
      setStatusComment('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleSaveAnalysis = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sav/${sav.id}/analysis`, analysis);
      toast.success('Analyse enregistrée');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleSaveDecision = async () => {
    if (!decision.type) { toast.error('Selectionnez un type de decision'); return; }
    if (!decision.justification?.trim()) { toast.error('Justification obligatoire'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/sav/${sav.id}/decision`, { ...decision, is_internal_error: isInternalError });
      toast.success('Decision enregistree' + (isInternalError ? ' — Refabrication URGENTE generee' : ' — Refabrication generee'));
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const startOverride = () => {
    const d = sav.decision || {};
    setOverrideData({
      type: d.type || '',
      justification: d.justification || '',
      amount_ht: d.amount_ht || 0,
      tva: d.tva || 20,
      fees: d.fees || 0,
      discount_amount: d.discount_amount || 0,
      override_reason: '',
    });
    setOverrideMode(true);
  };

  const handleSaveOverride = async () => {
    if (!overrideData.type) { toast.error('Selectionnez un type de decision'); return; }
    if (!overrideData.justification?.trim()) { toast.error('Justification obligatoire'); return; }
    if (!overrideData.override_reason?.trim()) { toast.error('Motif de modification obligatoire'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/sav/${sav.id}/decision`, { ...overrideData });
      toast.success('Decision modifiee — Refabrication mise a jour');
      setOverrideMode(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleFinalizeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFinalizingUpload(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const side = sav.ear_side === 'left' ? 'left' : 'right';
      await axios.post(`${API}/sav/${sav.id}/finalize-upload?side=${side}`, fd);
      toast.success('STL SAV uploade');
      onRefresh();
    } catch (err) {
      console.error('SAV STL upload error:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = detail || err.message || 'Erreur inconnue';
      toast.error(`Erreur upload STL${status ? ` (HTTP ${status})` : ''}: ${msg}`);
    }
    finally { setFinalizingUpload(false); if (finalizeFileRef.current) finalizeFileRef.current.value = ''; }
  };

  const handleSendToClient = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/sav/${sav.id}/send-to-client`, { admin_note: finalizeNote });
      toast.success('Visuel envoye au client — SAV finalise');
      onRefresh();
      fetchValidationFiles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  // Validation3D files for the refab order (Visualiser + Valider admin)
  const [validationFiles, setValidationFiles] = useState([]);
  const [validatingFileId, setValidatingFileId] = useState(null);
  const refabOrderId = sav.refabrication_order_id;

  const fetchValidationFiles = React.useCallback(async () => {
    if (!refabOrderId) return;
    try {
      const res = await axios.get(`${API}/orders/${refabOrderId}/validation3d`);
      setValidationFiles(res.data || []);
    } catch {
      setValidationFiles([]);
    }
  }, [refabOrderId]);

  useEffect(() => { fetchValidationFiles(); }, [fetchValidationFiles]);

  const openViewer = (token) => {
    if (!token) { toast.error('Token manquant'); return; }
    window.open(`/viewer/${token}`, '_blank', 'noopener,noreferrer');
  };

  const handleAdminValidate = async (fileId) => {
    setValidatingFileId(fileId);
    try {
      await axios.post(`${API}/validation3d/${fileId}/admin-validate`);
      toast.success('Modèle validé (admin). SAV clôturée si tous les côtés validés.');
      await fetchValidationFiles();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de valider');
    } finally { setValidatingFileId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sav-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono">{sav.sav_number}</span>
            {sav.urgency === 'urgent' && <span className="text-xs font-bold uppercase px-2 py-0.5 bg-rose-100 text-rose-700 rounded-sm">Urgent</span>}
            <span className={`text-xs font-medium px-2 py-0.5 rounded-sm ${STATUS_COLORS[sav.status]}`}>
              {STATUS_LABELS[sav.status]}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Bloc 1: Infos commande */}
          <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm" data-testid="sav-bloc-commande">
            <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Commande d'origine</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-zinc-400 text-xs">Patient</span><p className="font-medium">{sav.order_patient_ref}</p></div>
              <div><span className="text-zinc-400 text-xs">Produit</span><p className="font-medium">{sav.product_name}</p></div>
              <div><span className="text-zinc-400 text-xs">N° Série</span><p className="font-mono">{sav.serial_number || '—'}</p></div>
              <div><span className="text-zinc-400 text-xs">Oreille</span><p>{sav.ear_side === 'left' ? 'Gauche' : sav.ear_side === 'right' ? 'Droit' : 'Bilatéral'}</p></div>
              <div><span className="text-zinc-400 text-xs">Client</span><p>{sav.client_name}{sav.company_name ? ` — ${sav.company_name}` : ''}</p></div>
              <div><span className="text-zinc-400 text-xs">Livraison</span><p>{formatDate(sav.order_completed_at)}</p></div>
              {sav.order_material && <div><span className="text-zinc-400 text-xs">Matière</span><p>{sav.order_material}</p></div>}
              {sav.order_embout_type && <div><span className="text-zinc-400 text-xs">Type</span><p className="capitalize">{sav.order_embout_type}</p></div>}
              <div className="col-span-2"><span className="text-zinc-400 text-xs">Dispositif(s) concerné(s)</span>
                <p className="flex gap-2 flex-wrap" data-testid="sav-devices">
                  {(sav.devices || []).length ? sav.devices.map(d => <a key={d.hamac_id || d.serial_number} href={`/device/${encodeURIComponent(d.hamac_id || d.serial_number)}`} target="_blank" rel="noreferrer" className="font-mono text-xs px-1.5 py-0.5 bg-zinc-900 text-white rounded-sm hover:bg-zinc-700" data-testid={`sav-device-link-${d.hamac_id}`}>{d.hamac_id} · {d.side === 'left' ? 'G' : 'D'}</a>) : <span className="text-xs italic text-zinc-400">Non rattaché (réclamation antérieure à la traçabilité)</span>}
                </p>
              </div>
              {sav.commercial_eligibility && <div className="col-span-2"><span className="text-zinc-400 text-xs">Éligibilité commerciale</span><p className={`text-xs ${sav.commercial_eligibility.eligible ? 'text-emerald-700' : 'text-zinc-700'}`} data-testid="sav-eligibility-admin">{sav.commercial_eligibility.note}{sav.commercial_eligibility.expires_at ? ` (jusqu'au ${formatDate(sav.commercial_eligibility.expires_at)})` : ''}</p></div>}
            </div>
          </div>

          <QualityReviewBlock sav={sav} onRefresh={onRefresh} />

          {/* Bloc 2: Demande client */}
          <div className="bg-white border border-zinc-200 p-4 rounded-sm" data-testid="sav-bloc-demande">
            <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Demande client</h3>
            <div className="space-y-2 text-sm">
              <div className="flex gap-4">
                <div><span className="text-zinc-400 text-xs">Type</span><p className="font-medium">{REQUEST_TYPE_LABELS[sav.request_type]}</p></div>
                <div><span className="text-zinc-400 text-xs">Date problème</span><p>{formatDate(sav.problem_date)}</p></div>
                <div><span className="text-zinc-400 text-xs">Retour souhaité</span><p>{sav.wants_return ? 'Oui' : 'Non'}</p></div>
              </div>
              <div><span className="text-zinc-400 text-xs">Description</span><p className="mt-1 whitespace-pre-wrap bg-zinc-50 p-3 rounded-sm text-sm">{sav.description}</p></div>
              {sav.attachments?.length > 0 && (
                <div>
                  <span className="text-zinc-400 text-xs">Pièces jointes ({sav.attachments.length})</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {sav.attachments.map(a => (
                      <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline hover:text-blue-800">{a.filename}</a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {sav.suggested_decision && !sav.decision && (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 p-3 rounded-sm flex items-center gap-2" data-testid="sav-suggestion">
                <CheckCircle size={16} className="text-emerald-600" />
                <p className="text-sm text-emerald-800">Suggestion auto : <strong>Refabrication gratuite</strong> (refabrication identique)</p>
              </div>
            )}
          </div>

          {/* Bloc 3: Analyse */}
          <div className="bg-white border border-zinc-200 p-4 rounded-sm" data-testid="sav-bloc-analyse">
            <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Analyse interne</h3>
            <div className="space-y-3">
              <div>
                <Label className="label-overline mb-1 block">Commentaire interne</Label>
                <textarea value={analysis.internal_comment} onChange={(e) => setAnalysis({ ...analysis, internal_comment: e.target.value })} className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-20 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" data-testid="sav-analysis-comment" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline mb-1 block">Cause estimée</Label>
                  <Input value={analysis.estimated_cause} onChange={(e) => setAnalysis({ ...analysis, estimated_cause: e.target.value })} className="rounded-sm" data-testid="sav-analysis-cause" />
                </div>
                <div>
                  <Label className="label-overline mb-1 block">Responsable</Label>
                  <Input value={analysis.assigned_name} onChange={(e) => setAnalysis({ ...analysis, assigned_name: e.target.value })} className="rounded-sm" data-testid="sav-analysis-assigned" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={analysis.needs_return} onCheckedChange={(v) => setAnalysis({ ...analysis, needs_return: v })} />
                  <span className="text-sm">Besoin de retour produit</span>
                </label>
                <Button variant="outline" size="sm" className="ml-auto rounded-sm" onClick={handleSaveAnalysis} disabled={saving} data-testid="sav-save-analysis">
                  Enregistrer l'analyse
                </Button>
              </div>
            </div>
          </div>

          {/* Bloc 4: Décision */}
          <div className="bg-white border border-amber-200 p-4 rounded-sm" data-testid="sav-bloc-decision">
            <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-amber-200 text-amber-900 flex items-center justify-between">
              <span>Décision (obligatoire)</span>
              {sav.decision && isAdmin && !overrideMode && (
                <Button variant="outline" size="sm" onClick={startOverride}
                  className="rounded-sm text-xs h-7 border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="sav-override-btn">
                  Modifier la decision
                </Button>
              )}
            </h3>
            {sav.decision && !overrideMode ? (
              <div className="space-y-2 text-sm">
                <div className="flex gap-4 flex-wrap">
                  <div><span className="text-zinc-400 text-xs">Type</span><p className="font-bold">{DECISION_LABELS[sav.decision.type]}</p></div>
                  <div><span className="text-zinc-400 text-xs">Par</span><p>{sav.decision.decided_by_name}</p></div>
                  <div><span className="text-zinc-400 text-xs">Date</span><p>{formatDate(sav.decision.decided_at)}</p></div>
                </div>
                <div><span className="text-zinc-400 text-xs">Justification</span><p className="bg-zinc-50 p-2 rounded-sm mt-1">{sav.decision.justification}</p></div>
                {sav.decision.type === 'refabrication_payante' && (
                  <div className="flex gap-4">
                    <div><span className="text-zinc-400 text-xs">Montant HT</span><p className="font-bold">{sav.decision.amount_ht} €</p></div>
                    <div><span className="text-zinc-400 text-xs">TVA</span><p>{sav.decision.tva} %</p></div>
                    <div><span className="text-zinc-400 text-xs">Frais</span><p>{sav.decision.fees} €</p></div>
                  </div>
                )}
                {sav.decision.type === 'geste_commercial' && (
                  <div><span className="text-zinc-400 text-xs">Montant remise</span><p className="font-bold">{sav.decision.discount_amount} €</p></div>
                )}
                {sav.decision.override_reason && (
                  <div className="mt-2 bg-blue-50 border border-blue-200 p-2 rounded-sm text-xs">
                    <span className="font-semibold text-blue-900">Decision revisee :</span>
                    <span className="text-blue-800"> {sav.decision.override_reason}</span>
                  </div>
                )}
                {/* Historique des decisions precedentes */}
                {sav.decision_history?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-200">
                    <p className="text-xs font-semibold text-zinc-600 mb-2" data-testid="sav-decision-history">Historique des decisions ({sav.decision_history.length})</p>
                    <div className="space-y-1.5">
                      {sav.decision_history.map((h, i) => (
                        <div key={`${h.archived_at || h.decided_at || ''}-${i}`} className="text-xs bg-zinc-50 border border-zinc-200 p-2 rounded-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-zinc-700">{DECISION_LABELS[h.type] || h.type}</span>
                            <span className="text-zinc-400">{formatDate(h.archived_at || h.decided_at)}</span>
                          </div>
                          <p className="text-zinc-500 italic">Par {h.decided_by_name} — remplacee par {h.replaced_by}</p>
                          {h.override_reason && <p className="text-zinc-600 mt-1"><strong>Motif :</strong> {h.override_reason}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : overrideMode ? (
              <div className="space-y-3 bg-amber-50 p-3 rounded-sm border border-amber-300" data-testid="sav-override-form">
                <p className="text-xs text-amber-800 font-medium">Modification de la decision — La refabrication liee sera mise a jour automatiquement.</p>
                <div>
                  <Label className="label-overline mb-1 block">Nouveau type *</Label>
                  <Select value={overrideData.type} onValueChange={(v) => setOverrideData({ ...overrideData, type: v })}>
                    <SelectTrigger className="rounded-sm bg-white" data-testid="sav-override-type"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="refabrication_gratuite">Refabrication gratuite</SelectItem>
                      <SelectItem value="refabrication_payante">Refabrication payante</SelectItem>
                      <SelectItem value="geste_commercial">Geste commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-overline mb-1 block">Justification *</Label>
                  <textarea value={overrideData.justification} onChange={(e) => setOverrideData({ ...overrideData, justification: e.target.value })}
                    className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-16 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                    data-testid="sav-override-justification" />
                </div>
                {overrideData.type === 'refabrication_payante' && (
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="label-overline mb-1 block">HT</Label><Input type="number" step="0.01" value={overrideData.amount_ht} onChange={(e) => setOverrideData({ ...overrideData, amount_ht: parseFloat(e.target.value) || 0 })} className="rounded-sm" data-testid="sav-override-amount" /></div>
                    <div><Label className="label-overline mb-1 block">TVA</Label><Input type="number" value={overrideData.tva} onChange={(e) => setOverrideData({ ...overrideData, tva: parseFloat(e.target.value) || 0 })} className="rounded-sm" /></div>
                    <div><Label className="label-overline mb-1 block">Frais</Label><Input type="number" step="0.01" value={overrideData.fees} onChange={(e) => setOverrideData({ ...overrideData, fees: parseFloat(e.target.value) || 0 })} className="rounded-sm" /></div>
                  </div>
                )}
                {overrideData.type === 'geste_commercial' && (
                  <div>
                    <Label className="label-overline mb-1 block">Remise</Label>
                    <Input type="number" step="0.01" value={overrideData.discount_amount} onChange={(e) => setOverrideData({ ...overrideData, discount_amount: parseFloat(e.target.value) || 0 })} className="rounded-sm" />
                  </div>
                )}
                <div>
                  <Label className="label-overline mb-1 block text-rose-700">Motif de modification *</Label>
                  <textarea value={overrideData.override_reason} onChange={(e) => setOverrideData({ ...overrideData, override_reason: e.target.value })}
                    placeholder="Pourquoi modifiez-vous la decision initiale ?"
                    className="w-full p-3 border border-rose-200 bg-white rounded-sm resize-none h-14 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                    data-testid="sav-override-reason" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveOverride} disabled={saving}
                    className="rounded-sm bg-amber-600 hover:bg-amber-700 flex-1" data-testid="sav-override-save">
                    {saving ? 'Enregistrement...' : 'Valider la modification'}
                  </Button>
                  <Button variant="outline" onClick={() => setOverrideMode(false)} className="rounded-sm" data-testid="sav-override-cancel">
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="label-overline mb-1 block">Type de solution *</Label>
                  <Select value={decision.type} onValueChange={(v) => setDecision({ ...decision, type: v })}>
                    <SelectTrigger className="rounded-sm bg-white" data-testid="sav-decision-type"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="refabrication_gratuite">Refabrication gratuite</SelectItem>
                      <SelectItem value="refabrication_payante">Refabrication payante</SelectItem>
                      <SelectItem value="geste_commercial">Geste commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-overline mb-1 block">Justification *</Label>
                  <textarea value={decision.justification} onChange={(e) => setDecision({ ...decision, justification: e.target.value })} placeholder="Expliquer la décision..." className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-16 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" data-testid="sav-decision-justification" />
                </div>
                {decision.type === 'refabrication_payante' && (
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="label-overline mb-1 block">Montant HT</Label><Input type="number" step="0.01" value={decision.amount_ht} onChange={(e) => setDecision({ ...decision, amount_ht: parseFloat(e.target.value) || 0 })} className="rounded-sm" data-testid="sav-decision-amount" /></div>
                    <div><Label className="label-overline mb-1 block">TVA %</Label><Input type="number" value={decision.tva} onChange={(e) => setDecision({ ...decision, tva: parseFloat(e.target.value) || 0 })} className="rounded-sm" /></div>
                    <div><Label className="label-overline mb-1 block">Frais</Label><Input type="number" step="0.01" value={decision.fees} onChange={(e) => setDecision({ ...decision, fees: parseFloat(e.target.value) || 0 })} className="rounded-sm" /></div>
                  </div>
                )}
                {decision.type === 'geste_commercial' && (
                  <div>
                    <Label className="label-overline mb-1 block">Montant de remise</Label>
                    <Input type="number" step="0.01" value={decision.discount_amount} onChange={(e) => setDecision({ ...decision, discount_amount: parseFloat(e.target.value) || 0 })} className="rounded-sm" data-testid="sav-decision-discount" />
                  </div>
                )}
                <Button onClick={handleSaveDecision} disabled={saving} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 w-full" data-testid="sav-save-decision">
                  Valider la decision
                </Button>
                <div className="mt-2 bg-rose-50 border border-rose-200 p-3 rounded-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={isInternalError} onCheckedChange={(v) => setIsInternalError(v)} data-testid="sav-internal-error" />
                    <span className="text-sm text-rose-800 font-medium">Prime technicien perdue — Erreur de fabrication interne</span>
                  </label>
                  <p className="text-[10px] text-rose-500 mt-1 ml-6">
                    <strong>Coché (par défaut) :</strong> la refabrication est marquée URGENTE, la prime de la fab initiale est retirée au technicien (-2€/embout) et les nouvelles commandes peuvent être bloquées.<br/>
                    <strong>Décoché :</strong> le technicien conserve sa prime initiale (cas client : casse, usure, mauvaise prise d'empreinte…).
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Bloc 5: Finalisation SAV (upload STL + envoi client) */}
          {sav.decision && !sav.sav_finalized && (
            <div className="bg-white border-2 border-violet-300 p-4 rounded-sm" data-testid="sav-bloc-finalize">
              <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-violet-200 text-violet-900">Finalisation SAV — Upload STL + Envoi client</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-zinc-500 mb-2">Uploadez le STL de la refabrication avant de l'envoyer au client pour validation.</p>
                  <input ref={finalizeFileRef} type="file" accept=".stl" className="hidden" onChange={handleFinalizeUpload} data-testid="sav-finalize-file" />
                  <Button variant="outline" className="rounded-sm w-full text-xs" disabled={finalizingUpload}
                    onClick={() => finalizeFileRef.current?.click()} data-testid="sav-finalize-upload-btn">
                    {finalizingUpload ? 'Upload...' : 'Uploader STL refabrication'}
                  </Button>
                </div>
                {sav.finalization_stl_files?.length > 0 && (
                  <div className="space-y-1">
                    {sav.finalization_stl_files.map((f, i) => (
                      <div key={f.id || i} className="flex items-center gap-2 text-xs bg-violet-50 p-2 rounded-sm border border-violet-200">
                        <CheckCircle size={14} className="text-violet-600" weight="fill" />
                        <span className="font-medium">{f.filename}</span>
                        <span className="text-violet-400 ml-auto">{f.side === 'left' ? 'OG' : 'OD'}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <p className="text-xs text-zinc-400 mb-1">Note / commentaire pour le client (optionnel)</p>
                  <textarea value={finalizeNote} onChange={(e) => setFinalizeNote(e.target.value)} className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-16 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="Ex: Modifie selon vos retours..." data-testid="sav-finalize-note" />
                </div>
                <Button onClick={handleSendToClient} disabled={saving || !sav.sav_stl_uploaded}
                  className="rounded-sm bg-violet-600 hover:bg-violet-700 w-full text-white" data-testid="sav-send-to-client-btn">
                  <PaperPlaneTilt size={16} className="mr-2" />Envoyer le visuel au client
                </Button>
              </div>
            </div>
          )}

          {sav.sav_finalized && (
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm flex items-center gap-2" data-testid="sav-finalized-badge">
              <CheckCircle size={18} className="text-emerald-600" weight="fill" />
              <div>
                <p className="text-sm font-medium text-emerald-800">SAV finalise — Visuel envoye au client</p>
                {sav.finalized_at && <p className="text-xs text-emerald-600">Le {new Date(sav.finalized_at).toLocaleDateString('fr-FR')} par {sav.finalized_by}</p>}
                {sav.admin_note && <p className="text-xs text-emerald-700 mt-1 italic">Note: {sav.admin_note}</p>}
              </div>
            </div>
          )}

          {/* Bloc 5bis: Modèles 3D envoyés — Visualiser / Valider (admin) */}
          {validationFiles.length > 0 && (
            <div className="bg-white border-2 border-blue-200 p-4 rounded-sm" data-testid="sav-bloc-validation-3d">
              <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-blue-200 text-blue-900 flex items-center gap-2">
                <Eye size={16} />Modèles 3D SAV ({validationFiles.length})
              </h3>
              <p className="text-[11px] text-zinc-500 mb-3">Vous pouvez visualiser les modèles envoyés au client et les valider manuellement si nécessaire.</p>
              <div className="space-y-2">
                {[...validationFiles].sort((a, b) => {
                  if (a.side !== b.side) return a.side === 'left' ? -1 : 1;
                  return (b.version || 0) - (a.version || 0);
                }).map((f) => (
                  <div key={f.id} className="flex flex-wrap items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-sm">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium truncate">{f.original_filename || 'modèle.stl'}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-white text-blue-700 border border-blue-200">V{f.version || 1}</span>
                        <span className="text-[10px] text-zinc-500">{f.side === 'left' ? 'OG' : 'OD'}</span>
                        {f.status === 'validated' && (
                          <span className="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">
                            <CheckCircle size={10} className="mr-0.5" weight="fill" />Validé
                          </span>
                        )}
                        {f.status === 'modifications_requested' && (
                          <span className="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700">
                            Modification demandée
                          </span>
                        )}
                        {f.status === 'sent' && (
                          <span className="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">
                            En attente validation
                          </span>
                        )}
                      </div>
                      {f.validated_at && (
                        <span className="text-[10px] text-emerald-600">Validé le {new Date(f.validated_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      )}
                      {f.client_comment && (
                        <p className="text-[11px] text-amber-700 mt-0.5 italic">Commentaire client : {f.client_comment}</p>
                      )}
                    </div>
                    <Button size="sm" variant="outline"
                      onClick={() => openViewer(f.token)}
                      disabled={!f.token || ['revoked', 'expired'].includes(f.status)}
                      className="rounded-sm h-8 text-xs"
                      data-testid={`admin-view-stl-${f.id}`}>
                      <Eye size={12} className="mr-1" />Visualiser
                    </Button>
                    {f.status !== 'validated' && !['revoked', 'expired'].includes(f.status) && (
                      <Button size="sm"
                        onClick={() => handleAdminValidate(f.id)}
                        disabled={validatingFileId === f.id}
                        className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                        data-testid={`admin-validate-stl-${f.id}`}>
                        <CheckCircle size={12} className="mr-1" weight="fill" />
                        {validatingFileId === f.id ? 'Validation…' : 'Valider (admin)'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bloc 6: Changer statut */}
          <div className="bg-white border border-zinc-200 p-4 rounded-sm" data-testid="sav-bloc-statut">
            <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Changer le statut</h3>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="sav-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {SAV_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Commentaire (optionnel)" value={statusComment} onChange={(e) => setStatusComment(e.target.value)} className="flex-1 rounded-sm" />
              <Button variant="outline" size="sm" className="rounded-sm" onClick={handleStatusChange} disabled={saving || status === sav.status} data-testid="sav-update-status">
                Mettre à jour
              </Button>
            </div>
          </div>

          {/* Historique des statuts */}
          {sav.status_history?.length > 0 && (
            <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
              <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Historique</h3>
              <div className="space-y-2">
                {[...sav.status_history].reverse().map((h, i) => (
                  <div key={`${h.changed_at}-${h.status}`} className="flex items-start gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full bg-zinc-400 mt-1.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium">{STATUS_LABELS[h.status] || h.status}</span>
                      <span className="text-zinc-400"> — {formatDate(h.changed_at)} — {h.changed_by_name}</span>
                      {h.comment && <p className="text-zinc-500 italic mt-0.5">{h.comment}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
